import { gql } from "@apollo/client";
import { STANDARDIZED_FUNGIBLE_TOKEN_ANCHOR_FIELDS, STANDARDIZED_PREDICTION_EVENT_NAVIGATION_FIELDS, } from "../graphql/instrumentNavigationFragments.js";
/**
 * Minimal instrument selection for Signal / For You feed rows (`listSignals`,
 * `signalFeedUpdates`). Omits full prediction `markets[]`, price/volume
 * timeseries starts, and other detail-page fields — row renderers use
 * `eventTitle`, `title`, and `signalFeedMarketLabel` instead.
 */
export const SIGNAL_FEED_INSTRUMENT_FIELDS = gql `
  fragment SignalFeedInstrumentFields on StandardizedInstrument {
    id
    type
    instrument {
      __typename
      ... on StandardizedFungibleToken {
        id
        standardizedInstrumentId
        verified
        currency {
          decimals
          logoUrl
          asset {
            symbol
            name
          }
        }
        currentMarketCap {
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        ...StandardizedFungibleTokenAnchorFields
      }
      ... on StandardizedPerpetual {
        standardizedInstrumentId
        verified
        currency {
          # decimals is REQUIRED: extractStandardizedPerpetual →
          # Currency.toCurrency hard-invariants on it ("DECIMALS"), and one
          # perp edge missing it crashes the whole For You rail. This
          # selection must also stay shape-identical to the canonical perp
          # currency selection (GetStandardizedPerpetualLightQuery): Currency
          # is embedded (keyFields: false), so Apollo REPLACES the object on
          # every write — a narrower selection here silently wipes fields
          # other perp screens already cached.
          decimals
          logoUrl
          asset {
            symbol
            name
          }
        }
      }
      ... on StandardizedPredictionEvent {
        id
        standardizedInstrumentId
        ...StandardizedPredictionEventNavigationFields
        title
        description
      }
    }
  }
  ${STANDARDIZED_FUNGIBLE_TOKEN_ANCHOR_FIELDS}
  ${STANDARDIZED_PREDICTION_EVENT_NAVIGATION_FIELDS}
`;
export const SIGNAL_FEED_ACTIVITY_ITEM_FIELDS = gql `
  fragment SignalFeedActivityItemFields on SignalItem {
    __typename
    ... on Trade {
      balanceImpact
      timestamp
      amount {
        value
        token {
          address
          platformId
          currency {
            decimals
            asset {
              symbol
              name
            }
          }
        }
        amountInFiatCurrency {
          value
          currency {
            decimals
            asset {
              symbol
            }
          }
        }
      }
    }
    ... on PerpTrade {
      perpSymbol: symbol
      perpSide: side
      perpTimestamp: timestamp
      perpValue: value {
        value
        currency {
          decimals
          asset {
            symbol
          }
        }
      }
      # Position context (migration 0061): direction/action are derived
      # server-side from the fill against the position before it; closedPnl
      # is the venue-reported realized PnL on reducing/closing fills. All
      # nullable — realtime tape rows carry no position context and the row
      # falls back to the bare BUY/SELL chip.
      positionDirection
      positionAction
      closedPnl {
        value
        currency {
          decimals
          asset {
            symbol
          }
        }
      }
    }
    ... on PredictionTradeItem {
      predTimestamp: timestamp
      predSide: side
      predBalanceImpact: balanceImpact
      predQuantity: quantity
      predPrice: price
      predFiatValue: fiatValue
      eventTitle
      predOutcome: outcome {
        externalTokenId
        label
      }
    }
    # Aggregated cohort rows (served only when the operation opts in via
    # input.includeClusters). The host SignalsItem.profile is null for
    # these; sampleProfiles carries the avatar-stack wallets instead. The
    # SignalFeedProfileFields definition is NOT interpolated here — every
    # operation embedding this fragment already interpolates it once, and
    # a second copy would be a duplicate-fragment error.
    ... on SignalCluster {
      clusterId
      side
      traderCount
      totalNotionalUsd
      windowStartedAt
      lastEventAt
      sampleProfiles {
        ...SignalFeedProfileFields
      }
    }
  }
`;
export const SIGNAL_FEED_PROFILE_FIELDS = gql `
  fragment SignalFeedProfileFields on WalletProfile {
    accounts {
      id
      address
      vmType
      type
      platformId
    }
    displayName
    avatarUrl
    verified
  }
`;
